import React, { useState } from 'react';
import styled from '@emotion/styled';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LoginForm } from './components/Auth/LoginForm';
import { AuthCallback } from './components/Auth/AuthCallback';
import { HomePage } from './pages/HomePage';
import { GraphPage } from './pages/GraphPage';
import { injectThemeAnimations } from './services/advancedThemeGenerator';
import { loadGoogleFonts } from './services/fontLoader';
import { designSystems } from './services/designSystems';
import { sessionTracker } from './services/sessionTracker';
import { memoryService } from './services/memoryService';
import { mixpanelService } from './services/mixpanel';
import { activityTracker } from './services/activityTracker';
import { ConsentBanner } from './components/ConsentBanner';
import './App.css';

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--color-background);
  transition: var(--transition);
`;

function App() {
  const [authReady, setAuthReady] = useState(false);

  // Inject theme animations and load default fonts on mount
  React.useEffect(() => {
    injectThemeAnimations();
    
    // Initialize Mixpanel
    mixpanelService.init();
    
    // Track app initialization (will be blocked until consent is given)
    mixpanelService.track('app_initialized', {
      environment: process.env.NODE_ENV,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`
    });
    
    // Load default theme fonts
    const defaultFonts = designSystems.DEFAULT.typography.googleFonts;
    loadGoogleFonts(defaultFonts).catch(err => {
      console.warn('Failed to load default fonts:', err);
    });
  }, []);

  // Wait for auth to be ready before rendering
  React.useEffect(() => {
    const timer = setTimeout(() => setAuthReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (!authReady) {
    return <AppContainer />;
  }

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AuthWrapper />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

function AuthWrapper() {
  const { user, loading } = useAuth();
  
  const handleConsentChange = (status: 'granted' | 'denied' | 'pending') => {
    console.log('[App] Consent changed to:', status);
    
    // Track consent decision (will only work if consent was granted)
    mixpanelService.track('consent_decision', {
      decision: status,
      timestamp: new Date().toISOString()
    });
  };

  // Initialize session tracking when user is logged in
  React.useEffect(() => {
    if (user && !loading) {
      sessionTracker.startSession();
      activityTracker.startTracking(); // Start activity tracking
      
      // Save session on unmount or when user changes
      return () => {
        activityTracker.stopTracking(); // Stop activity tracking
        memoryService.updateRabbitHoleStats('session_end');
        sessionTracker.cleanup();
      };
    }
  }, [user, loading]);

  // Handle page visibility to track session time
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      if (user) {
        activityTracker.stopTracking(); // Stop activity tracking
        memoryService.updateRabbitHoleStats('session_end');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user]);

  // Always render routes, but protect individual routes
  return (
    <>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route 
          path="/" 
          element={
            loading ? (
              <AppContainer>
                <div style={{ color: 'var(--color-text)' }}>Loading...</div>
              </AppContainer>
            ) : user ? (
              <HomePage />
            ) : (
              <AppContainer>
                <LoginForm />
              </AppContainer>
            )
          } 
        />
        <Route 
          path="/graph" 
          element={
            loading ? (
              <AppContainer>
                <div style={{ color: 'var(--color-text)' }}>Loading...</div>
              </AppContainer>
            ) : user ? (
              <GraphPage />
            ) : (
              <AppContainer>
                <LoginForm />
              </AppContainer>
            )
          } 
        />
      </Routes>
      
      {/* Consent Banner - shows globally */}
      <ConsentBanner onConsentChange={handleConsentChange} />
    </>
  );
}

export default App;